import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCms } from "@/context/CmsContext";
import { PageHeader, SectionCard, Loading } from "@/components/common/ui";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search, Check, RotateCcw, Loader2, LayoutDashboard, BarChart3, Building2, FileText, ExternalLink, Radio,
} from "lucide-react";

const SECTION_ICON = {
  dashboard: LayoutDashboard,
  consolidated: Building2,
  analytics: BarChart3,
  project_detail: FileText,
};

// Where each CMS section actually appears — so an admin can jump straight to it.
const SECTION_LINK = {
  dashboard: { to: "/dashboard", label: "Open Dashboard" },
  consolidated: { to: "/dashboard", label: "Open Dashboard" },
  analytics: { to: "/analytics", label: "Open Analytics" },
  project_detail: { to: "/analytics?dim=project", label: "Open a Project" },
};

function ContentRow({ item, onSaved }) {
  const { setLocal } = useCms();
  const [val, setVal] = useState(item.value || "");
  const [busy, setBusy] = useState(false);
  const isMultiline = item.type === "multiline";
  const dirty = (val || "") !== (item.value || "");

  useEffect(() => { setVal(item.value || ""); }, [item.value]);

  const save = async () => {
    setBusy(true);
    try {
      await api.saveCmsContent({ key: item.key, value: val, reason: "cms panel" });
      setLocal(item.key, val);
      toast.success(`Saved · ${item.label}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.resetCmsContent(item.key);
      setLocal(item.key, "");
      toast.success(`Reset · ${item.label}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-2 rounded-lg border bg-card p-3 md:grid-cols-[220px_1fr]" data-testid={`cms-row-${item.key}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{item.label}</p>
        <p className="truncate text-[11px] text-muted-foreground" title={item.key}>{item.key}</p>
        {item.overridden
          ? <Badge variant="outline" className="mt-1 border-[hsl(var(--primary)/0.4)] text-[10px] text-[hsl(var(--primary))]">Customised</Badge>
          : <span className="mt-1 inline-block text-[10px] text-muted-foreground">Default</span>}
      </div>
      <div className="space-y-2">
        {isMultiline
          ? <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} className="text-sm" placeholder={item.default} data-testid={`cms-panel-input-${item.key}`} />
          : <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-9 text-sm" placeholder={item.default} data-testid={`cms-panel-input-${item.key}`} />}
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-[11px] text-muted-foreground">Default: <span className="italic">{item.default}</span></p>
          <div className="flex shrink-0 gap-1.5">
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={reset} disabled={busy || !item.overridden} data-testid={`cms-panel-reset-${item.key}`}>
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset
            </Button>
            <Button size="sm" className="h-8 px-3 text-xs" onClick={save} disabled={busy || !dirty} data-testid={`cms-panel-save-${item.key}`}>
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCms() {
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => api.cmsRegistry().then((d) => setReg(d)).catch(() => setReg(null));
  useEffect(() => { setLoading(true); api.cmsRegistry().then((d) => { setReg(d); setLoading(false); }).catch(() => setLoading(false)); }, []);

  // live refresh when any admin edits from elsewhere
  useEffect(() => {
    const onRt = (e) => { if (e?.detail?.type === "cms:update") load(); };
    window.addEventListener("realtime:update", onRt);
    return () => window.removeEventListener("realtime:update", onRt);
  }, []);

  const groups = useMemo(() => {
    const gs = reg?.groups || [];
    if (!q.trim()) return gs;
    const needle = q.trim().toLowerCase();
    return gs
      .map((g) => ({ ...g, items: g.items.filter((it) => `${it.label} ${it.key} ${it.current}`.toLowerCase().includes(needle)) }))
      .filter((g) => g.items.length > 0);
  }, [reg, q]);

  const totalKeys = (reg?.groups || []).reduce((a, g) => a + g.items.length, 0);
  const customised = (reg?.groups || []).reduce((a, g) => a + g.items.filter((i) => i.overridden).length, 0);

  if (loading && !reg) return <Loading />;

  return (
    <div className="space-y-5" data-testid="admin-cms">
      <PageHeader title="Content & CMS" description="Edit dashboard copy — titles, labels and descriptions — organised by section. Changes go live everywhere instantly.">
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-[hsl(var(--success)/0.1)] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--success))]" data-testid="cms-live-badge">
          <Radio className="h-3.5 w-3.5" /> Live sync
        </span>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search content by label or key…" className="h-9 pl-8" data-testid="cms-search" />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="cms-stats">{totalKeys} editable items · {customised} customised</span>
      </div>

      <SectionCard title="Editable content" description="Grouped by the section it appears in. Search to find any element fast." testId="cms-content-card">
        <div className="p-3">
          {groups.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No content matches “{q}”.</p>
          ) : (
            <Accordion type="multiple" defaultValue={groups.map((g) => g.section)} className="space-y-2">
              {groups.map((g) => {
                const Icon = SECTION_ICON[g.section] || FileText;
                const link = SECTION_LINK[g.section];
                return (
                  <AccordionItem key={g.section} value={g.section} className="rounded-lg border px-3" data-testid={`cms-group-${g.section}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <Icon className="h-4 w-4 text-[hsl(var(--primary))]" /> {g.label}
                        <Badge variant="secondary" className="ml-1 text-[10px]">{g.items.length}</Badge>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 pb-2">
                        {link && (
                          <Link to={link.to} className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] hover:underline" data-testid={`cms-open-${g.section}`}>
                            {link.label} <ExternalLink className="h-3 w-3" />
                          </Link>
                        )}
                        {g.items.map((it) => <ContentRow key={it.key} item={it} onSaved={load} />)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Structural controls" description="Layout, data and metric editors live where they’re used — jump straight to them." testId="cms-structural-card">
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { to: "/dashboard", icon: LayoutDashboard, title: "Dashboard layout & KPIs", desc: "Show/hide & reorder sections, edit KPI values, planned budgets (via the Manage panel)." },
            { to: "/analytics?dim=project", icon: FileText, title: "Project verticals & budgets", desc: "Add / rename / remove verticals and set planned budgets from any project page." },
            { to: "/budget-allocation", icon: BarChart3, title: "Budgets & allocation", desc: "Annual allocation, planned budgets and vertical weights." },
            { to: "/targets", icon: BarChart3, title: "Targets", desc: "Revenue, funnel and conversion targets." },
            { to: "/settings", icon: ExternalLink, title: "Settings & lists", desc: "Master lists, taxonomy, fiscal years, users and roles." },
            { to: "/governance/audit-log", icon: FileText, title: "Audit log", desc: "Every change is recorded here for traceability." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.title} to={c.to} className="group rounded-xl border bg-card p-4 transition-colors duration-150 hover:bg-[hsl(var(--surface-2))]" data-testid={`cms-quicklink-${c.to}`}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))]"><Icon className="h-4 w-4" /></span>
                  <p className="text-sm font-semibold">{c.title}</p>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{c.desc}</p>
              </Link>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}
